/**
 * AdaptiveDiagnosticsPage — Phase 8
 * Platform-admin cross-domain adaptive health dashboard.
 * Route: /coach/admin/adaptive-diagnostics
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import CoachLayout from './CoachLayout';
import { useAuth } from '../../context/AuthContext';
import { getAdaptiveDiagnostics } from '../../services/api';

const HEALTH_COLORS = {
  good:     { bg: 'rgba(34,197,94,0.15)',  border: 'rgba(34,197,94,0.4)',  text: '#4ade80' },
  warning:  { bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.4)', text: '#fbbf24' },
  critical: { bg: 'rgba(239,68,68,0.15)',  border: 'rgba(239,68,68,0.4)',  text: '#f87171' },
};

function HealthBadge({ health }) {
  const c = HEALTH_COLORS[health] || HEALTH_COLORS.good;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 999,
      fontSize: 11, fontWeight: 700, background: c.bg, border: `1px solid ${c.border}`,
      color: c.text, textTransform: 'capitalize',
    }}>
      {health}
    </span>
  );
}

function CoverageBar({ pct }) {
  const color = pct >= 80 ? '#4ade80' : pct >= 50 ? '#fbbf24' : '#f87171';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 70, height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: color, borderRadius: 999 }} />
      </div>
      <span style={{ fontSize: 12, color, fontWeight: 600 }}>{pct}%</span>
    </div>
  );
}

export default function AdaptiveDiagnosticsPage() {
  const { learner, isAdmin } = useAuth();

  // Allow platform_admin or admin role
  const isPlatformAdmin = isAdmin || learner?.role === 'platform_admin';

  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [lastRun, setLastRun]         = useState(null);

  const fetchDiagnostics = useCallback(() => {
    setLoading(true);
    setError(null);
    getAdaptiveDiagnostics()
      .then(({ data }) => {
        setDiagnostics(data);
        setLastRun(data.last_run || null);
      })
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load diagnostics'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (isPlatformAdmin) fetchDiagnostics();
  }, [isPlatformAdmin, fetchDiagnostics]);

  if (!isPlatformAdmin) {
    return <Navigate to="/coach" replace />;
  }

  const domains = diagnostics?.domains || [];

  const summaryStats = domains.reduce(
    (acc, d) => ({
      totalDomains: acc.totalDomains + 1,
      critical: acc.critical + (d.health === 'critical' ? 1 : 0),
      warning: acc.warning + (d.health === 'warning' ? 1 : 0),
      good: acc.good + (d.health === 'good' ? 1 : 0),
    }),
    { totalDomains: 0, critical: 0, warning: 0, good: 0 }
  );

  return (
    <CoachLayout title="Adaptive Diagnostics">
      <div data-testid="adaptive-diagnostics-page">

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, color: '#e2e8f0' }}>Adaptive System Health</h2>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              Cross-domain diagnostics — platform admin only
              {lastRun && (
                <span style={{ marginLeft: 8 }}>
                  · Last run: {new Date(lastRun).toLocaleString()}
                </span>
              )}
            </div>
          </div>
          <button
            data-testid="run-health-check-btn"
            className="btn btn-primary"
            onClick={fetchDiagnostics}
            disabled={loading}
            style={{ minWidth: 160 }}
          >
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                Running…
              </span>
            ) : (
              'Run System Health Check'
            )}
          </button>
        </div>

        {/* Summary cards */}
        {domains.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 24 }}>
            {[
              { label: 'Domains', value: summaryStats.totalDomains, color: '#818cf8' },
              { label: 'Healthy', value: summaryStats.good, color: '#4ade80' },
              { label: 'Warning', value: summaryStats.warning, color: '#fbbf24' },
              { label: 'Critical', value: summaryStats.critical, color: '#f87171' },
            ].map(({ label, value, color }) => (
              <div
                key={label}
                className="coach-stat-card"
                style={{ padding: 16, border: `1px solid ${color}22` }}
              >
                <span className="stat-value" style={{ fontSize: '1.6rem', color }}>{value}</span>
                <span className="stat-label" style={{ fontSize: 11 }}>{label}</span>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: 8, padding: '12px 16px', color: '#f87171', marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        {loading && <div className="coach-loading">Running health check across all domains…</div>}

        {/* Cross-domain health table */}
        {!loading && domains.length > 0 && (
          <div className="coach-section" style={{ margin: 0 }}>
            <div className="coach-section-header">
              <h2 className="coach-section-title">Domain Health Summary</h2>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="coach-table" data-testid="cross-domain-health-table">
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Modules</th>
                    <th>Skills</th>
                    <th>Learners</th>
                    <th>Item Coverage</th>
                    <th>Card Coverage</th>
                    <th>Health</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {domains.map((d) => (
                    <tr key={d.domain}>
                      <td style={{ fontWeight: 700, color: '#cbd5e1', textTransform: 'capitalize' }}>
                        {d.domain.replace(/_/g, ' ')}
                      </td>
                      <td style={{ color: '#94a3b8' }}>{d.module_count}</td>
                      <td style={{ color: '#94a3b8' }}>{d.skill_count}</td>
                      <td style={{ color: '#94a3b8' }}>{d.learner_count}</td>
                      <td><CoverageBar pct={d.item_coverage} /></td>
                      <td><CoverageBar pct={d.card_coverage} /></td>
                      <td><HealthBadge health={d.health} /></td>
                      <td>
                        <a
                          href={`/coach/workspace/${d.domain}/health`}
                          style={{ fontSize: 12, color: '#818cf8', textDecoration: 'none' }}
                        >
                          → Detail
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && !error && domains.length === 0 && !diagnostics && (
          <div className="coach-empty">
            <span className="coach-empty-icon">🔍</span>
            <div className="coach-empty-title">No diagnostics yet</div>
            <div className="coach-empty-desc">
              Click <strong>Run System Health Check</strong> to analyze adaptive system health across all domains.
            </div>
          </div>
        )}

        {!loading && diagnostics && domains.length === 0 && (
          <div style={{
            background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
            borderRadius: 8, padding: 20, textAlign: 'center', color: '#94a3b8',
          }}>
            No adaptive domains found. Seed domain configurations in the config_store to enable diagnostics.
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </CoachLayout>
  );
}
